/** The rep's navigation app, remembered on THIS device (APP-1, Oct 2026).
 *
 *  Device-level on purpose: navigation happens on the phone in the car, and a rep's work and
 *  personal phones (or head units) can differ.
 *
 *  `device` (the default) means "my phone decides": on Android, Take Me There hands the address
 *  to the phone, which opens its default navigation app or asks which one; an iPhone has no
 *  such chooser, so SIFT asks once (Google Maps, Waze or Apple Maps) and remembers the answer.
 */
export type NavApp = 'google' | 'waze' | 'apple';
export type NavPref = NavApp | 'device';

export const NAV_APPS: NavApp[] = ['google', 'waze', 'apple'];
export const NAV_PREFS: NavPref[] = ['device', ...NAV_APPS];

/** Used by the legacy maps.html hand-off page when the device has no remembered app. */
export const DEFAULT_NAV_APP: NavApp = 'google';
export const DEFAULT_NAV_PREF: NavPref = 'device';

export const NAV_APP_LABEL: Record<NavApp, string> = {
  google: 'Google Maps',
  waze: 'Waze',
  apple: 'Apple Maps',
};

export const NAV_PREF_LABEL: Record<NavPref, string> = {
  device: "My phone's default (ask if none)",
  ...NAV_APP_LABEL,
};

export const NAV_APP_KEY = 'sift.navApp';

const isNavApp = (value: unknown): value is NavApp =>
  typeof value === 'string' && (NAV_APPS as string[]).includes(value);

// Private browsing and locked-down mail webviews can throw on any storage access.
const store = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

export function getNavApp(storage: Storage | null = store()): NavApp | null {
  try {
    const value = storage?.getItem(NAV_APP_KEY);
    return isNavApp(value) ? value : null;
  } catch {
    return null;
  }
}

export function setNavApp(app: NavApp | null, storage: Storage | null = store()): void {
  try {
    if (app) storage?.setItem(NAV_APP_KEY, app);
    else storage?.removeItem(NAV_APP_KEY);
  } catch {
    // Not remembered; the rep is simply asked again next time.
  }
}

/** The device's preference: a remembered app, else `device`. Stored as the app or nothing. */
export function getNavPref(storage: Storage | null = store()): NavPref {
  return getNavApp(storage) ?? DEFAULT_NAV_PREF;
}

export function setNavPref(pref: NavPref, storage: Storage | null = store()): void {
  setNavApp(pref === 'device' ? null : pref, storage);
}
